// Packages
import { useEffect, useState, useContext } from 'react'
import { useRecoilValue, useRecoilState, useRecoilCallback } from 'recoil'
import { Helmet } from 'react-helmet-async'

// Language
import { Language } from '../../Language/Language'

// Store
import {
  filters,
  countryId,
  cityId,
  cuisineId,
  countryData,
  modalMerchants,
  showMerchantsModal,
  modalMerchant,
  filterHalal,
  merchantsData,
  searchQuery,
  userPostalCode,
  userCoordinates,
  sortMethod,
  postalCodeValidationError,
  draftCountryId,
  draftCityId,
  draftCuisineId,
  draftFilterHalal,
  draftSearchQuery,
  draftUserPostalCode,
  draftUserCoordinates,
  draftSortMethod,
  draftPostalCodeValidationError,
  draftCityData,
  draftCuisineData,
} from '../../Store/Store'

// Elements
import {
  ModalMask,
  Main,
  Flourish,
  Container,
  ControlsBox,
  ControlsClearLink,
  RefineTrigger,
  RefineTriggerText,
  RefineTitle,
  RefineSubtitle,
  SheetBackdrop,
  Sheet,
  SheetHeading,
  SheetHeadingTitle,
  SheetBody,
  SheetFooter,
  SheetCancelButton,
  SheetConfirmButton,
  SheetClearLink,
} from './Listing.elements'
import { IconChevronRight } from './Results.icons'
import { resolveInitialCountryId } from './Filters.helpers'

import Modal from './Modal'

import { BannerWrapper } from './BannerWrapper.elements'
import {
  PlatinumBanner,
  CenturionBanner,
  BannerCard,
  Button,
  Title,
  Paragraph,
} from './Banner.elements'

import { useParams } from 'react-router-dom'

// Components
import Hero from './Hero'
import Intro from './Intro'
import Filters from './Filters'
import SearchInput from './SearchInput'
// import SortControls from './SortControls'
import Results from './Results'
// import Pocket from "./Pocket";
import PocketCard from './PocketCard'
// import Temp from "./Temp";
import Footer from '../Common/Footer'
import LangSelect from '../Common/LangSelect'

// [real atom, draft atom] pairs for the mobile filter sheet. Opening the sheet
// copies real -> draft; Confirm copies draft -> real; Cancel discards drafts.
const DRAFT_PAIRS = [
  [searchQuery, draftSearchQuery],
  [userPostalCode, draftUserPostalCode],
  [userCoordinates, draftUserCoordinates],
  [sortMethod, draftSortMethod],
  [postalCodeValidationError, draftPostalCodeValidationError],
  [countryId, draftCountryId],
  [cityId, draftCityId],
  [cuisineId, draftCuisineId],
  [filterHalal, draftFilterHalal],
]

export default function Listing() {
  const { locale, benefit } = useParams()

  const {
    platinumBanner: platinumCopy,
    centurionBanner: centurionCopy,
    filters: filterCopy,
  } = useContext(Language)
  const { country } = useRecoilValue(filters)
  const [id, setCountryId] = useRecoilState(countryId)
  const countries = useRecoilValue(countryData)

  const groupedMerchants = useRecoilValue(modalMerchants)
  const groupMerchant = useRecoilValue(modalMerchant)
  const filterHalalMerchants = useRecoilValue(filterHalal)

  const [showModal, setShowModal] = useRecoilState(showMerchantsModal)
  const merchants = useRecoilValue(merchantsData(locale))

  const [width, setWidth] = useState(
    () => window.visualViewport?.width ?? window.innerWidth,
  )
  const [sheetOpen, setSheetOpen] = useState(false)
  // Bumping this remounts the sheet body so the (uncontrolled/local-state)
  // search and postcode inputs visibly reset when "Clear" resets their atoms.
  const [clearKey, setClearKey] = useState(0)
  const [isJP, setIsJP] = useState(false)
  const [countryCount, setCountryCount] = useState('0')
  const [platinumButtonUrl, setPlatinumButtonUrl] = useState('')
  const [centurionButtonUrl, setCenturionButtonUrl] = useState('')
  const isSBS = benefit === 'sbs-diningbenefit'

  function handleResize() {
    setWidth(window.visualViewport.width)
  }

  function showPlatinumBanner() {
    return !!platinumButtonUrl && !isSBS
  }

  function showCenturionBanner() {
    return !!centurionButtonUrl && !isSBS
  }

  function hasInHotelRestaurantMerchants(merchants) {
    if (merchants) {
      return merchants.some((merchant) => {
        return merchant.businessData.isInHotel
      })
    } else {
      return false
    }
  }

  useEffect(() => {
    setIsJP(locale.split('-')[1] === 'jp')
    let count = 20
    if (countries && countries.length) {
      let arr = []
      countries.forEach((c) => {
        arr = arr.concat([...c.countries])
      })
      arr = arr.filter(
        (f) => (f.isActive && f.isVisibleToOtherMarkets) || f.key === id,
        // f => f.isVisibleToOtherMarkets || f.key === id
      )
      // console.log(arr, arr.length);
      count = arr.length
    }
    setCountryCount('' + count + '')
  }, [country])

  useEffect(() => {
    handleResize()

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Initialise the country from the locale. This lives here (not only in the
  // Filters component) because on mobile the filters are unmounted inside the
  // closed sheet, so otherwise no country would be set on landing and no
  // merchants would show.
  useEffect(() => {
    if (id) return
    if (!countries || !countries.length) return
    setCountryId(resolveInitialCountryId(countries, locale))
  }, [countries, id, locale, setCountryId])

  useEffect(() => {
    const urls = {
      'en-gb':
        'https://www.americanexpress.com/en-gb/benefits/the-platinum-card/',
      'de-de':
        'https://www.amex-kreditkarten.de/infocenter/benefit-portal/platinum-card/intro',
      'en-au':
        'https://www.americanexpress.com/en-au/credit-cards/membership-benefits/the-platinum-card/',
      'en-sg': 'https://go.amex/myplatinum',
      'zh-tw': 'https://www.americanexpress.com/tw/platinum',
      'de-at': 'https://www.americanexpress.com/de-at/vorteile/platinum-card/',
      'ja-jp': 'https://www.americanexpress.com/ja-jp/benefits/platinum-card/',
      'en-ca':
        'https://www.americanexpress.com/ca/en/benefits/the-platinum-card/',
      'fr-ca':
        'https://www.americanexpress.com/ca/fr/avantages/la-carte-de-platine/',

      // "mx-es": "#",
      // "it-it": "#",
    }
    setPlatinumButtonUrl(urls[locale])
  }, [])

  useEffect(() => {
    const urls = {
      'de-at':
        'https://www.americanexpress.com/at/vorteile/centurion/index.html',
      // "mx-es": "#",
      // "it-it": "#",
    }
    setCenturionButtonUrl(urls[locale])
  }, [])

  useEffect(() => {
    const handleKeyUp = (e) => {
      if (
        showModal &&
        (e.keyCode === 27 || e.keyCode === 13 || e.keyCode === 32)
      ) {
        setShowModal(false)
      }
    }

    window.document.addEventListener('keyup', handleKeyUp)

    return () => window.removeEventListener('keyup', handleKeyUp)
  }, [showModal])

  useEffect(() => {
    if (showModal || sheetOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }, [showModal, sheetOpen])

  function closeModal() {
    window.tuiTrack.trackEvent('click', `closeGroupModal:${groupMerchant.name}`)
    setShowModal(false)
  }

  // Opening the sheet seeds the draft atoms from the current (real) values.
  const openSheet = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        DRAFT_PAIRS.forEach(([real, draft]) =>
          set(draft, snapshot.getLoadable(real).contents),
        )
        window.tuiTrack.trackEvent('click', 'refineSearch:open')
        setSheetOpen(true)
      },
    [],
  )

  // Cancel discards the drafts by simply not committing them — the real atoms
  // (which the results read) were never touched while the sheet was open.
  function cancelSheet() {
    setSheetOpen(false)
  }

  // Confirm applies the sheet's edits by copying the drafts onto the real atoms.
  const confirmSheet = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        DRAFT_PAIRS.forEach(([real, draft]) =>
          set(real, snapshot.getLoadable(draft).contents),
        )
        setSheetOpen(false)
      },
    [],
  )

  const clearAllFilters = useRecoilCallback(
    ({ set }) =>
      () => {
        // While the sheet is open, clear the draft set; otherwise (desktop)
        // clear the real atoms directly.
        const a = sheetOpen
          ? {
              search: draftSearchQuery,
              postal: draftUserPostalCode,
              coords: draftUserCoordinates,
              sort: draftSortMethod,
              err: draftPostalCodeValidationError,
              country: draftCountryId,
              city: draftCityId,
              cuisine: draftCuisineId,
              halal: draftFilterHalal,
            }
          : {
              search: searchQuery,
              postal: userPostalCode,
              coords: userCoordinates,
              sort: sortMethod,
              err: postalCodeValidationError,
              country: countryId,
              city: cityId,
              cuisine: cuisineId,
              halal: filterHalal,
            }
        set(a.search, '')
        set(a.postal, '')
        set(a.coords, null)
        set(a.sort, 'alphabetical')
        set(a.err, null)
        set(a.city, null)
        set(a.cuisine, null)
        set(a.halal, false)
        if (countries && countries.length) {
          set(a.country, resolveInitialCountryId(countries, locale))
        }
        window.tuiTrack.trackEvent('click', 'refineSearch:clearAll')
        setClearKey((k) => k + 1)
      },
    [countries, locale, sheetOpen],
  )

  return (
    <>
      <Helmet>
        <title>American Express - Dining Offer</title>
      </Helmet>
      <Main isJP={isJP} data-locale={locale}>
        {showModal && (
          <>
            <ModalMask />
            <Modal
              merchants={groupedMerchants}
              merchant={groupMerchant}
              closeModal={closeModal}
              locale={locale}
              country={country}
            ></Modal>
          </>
        )}
        <Hero />
        <Flourish />
        <Container>
          <Intro
            locale={locale}
            countryCount={countryCount}
            hasInHotel={hasInHotelRestaurantMerchants(merchants)}
            country={country}
          />
          {(() => {
            // On mobile (draft=true) the controls edit the draft atoms so the
            // results don't change until Confirm. On desktop they edit the real
            // atoms live (draft props omitted -> components use their defaults).
            const renderControls = (draft) => (
              <>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full">
                    <SearchInput
                      atom={draft ? draftSearchQuery : searchQuery}
                      immediate={draft}
                    />
                  </div>
                  {/* {country !== 'HK' && country !== 'JP' && (
                    <div className="w-full md:w-1/2">
                      <SortControls
                        locale={locale}
                        country={country}
                        {...(draft && {
                          postalCodeAtom: draftUserPostalCode,
                          coordinatesAtom: draftUserCoordinates,
                          sortMethodAtom: draftSortMethod,
                          validationErrorAtom: draftPostalCodeValidationError,
                        })}
                      />
                    </div>
                  )} */}
                </div>
                <Filters
                  asAccordion={draft}
                  {...(draft && {
                    countryAtom: draftCountryId,
                    cityAtom: draftCityId,
                    cuisineAtom: draftCuisineId,
                    halalAtom: draftFilterHalal,
                    citySelector: draftCityData,
                    cuisineSelector: draftCuisineData,
                  })}
                />
              </>
            )

            return width < 768 ? (
              <>
                <RefineTrigger id="filters" onClick={openSheet}>
                  <RefineTriggerText>
                    <RefineTitle>
                      {filterCopy.refineTitle || 'Refine your search'}
                    </RefineTitle>
                    <RefineSubtitle>
                      {filterCopy.refineSubtitle ||
                        'Filter restaurants by location or cuisine type'}
                    </RefineSubtitle>
                  </RefineTriggerText>
                  <IconChevronRight />
                </RefineTrigger>
                {sheetOpen && (
                  <>
                    <SheetBackdrop onClick={cancelSheet} />
                    <Sheet role="dialog" aria-modal="true">
                      <SheetHeading>
                        <SheetHeadingTitle>
                          {filterCopy.filterModalHeading || 'Filter Restaurants'}
                        </SheetHeadingTitle>
                        <SheetClearLink type="button" onClick={clearAllFilters}>
                          {filterCopy.clear || 'Clear'}
                        </SheetClearLink>
                      </SheetHeading>
                      <SheetBody key={clearKey}>
                        {renderControls(true)}
                      </SheetBody>
                      <SheetFooter>
                        <SheetCancelButton type="button" onClick={cancelSheet}>
                          {filterCopy.cancel || 'Cancel'}
                        </SheetCancelButton>
                        <SheetConfirmButton
                          type="button"
                          onClick={confirmSheet}
                        >
                          {filterCopy.confirm || 'Confirm'}
                        </SheetConfirmButton>
                      </SheetFooter>
                    </Sheet>
                  </>
                )}
              </>
            ) : (
              <ControlsBox key={clearKey}>
                <ControlsClearLink type="button" onClick={clearAllFilters}>
                  {filterCopy.clear || 'Clear'}
                </ControlsClearLink>
                {renderControls(false)}
              </ControlsBox>
            )
          })()}

          {country === 'JP' ? (
            <PocketCard locale={locale} />
          ) : (
            <Results
              locale={locale}
              country={country}
              filterHalalMerchants={filterHalalMerchants}
            />
          )}

          {showPlatinumBanner() && showCenturionBanner() && (
            <BannerWrapper isPair={true}>
              <PlatinumBanner paired={true}>
                <BannerCard>
                  <Title>{platinumCopy.title}</Title>
                  <Paragraph
                    paired={true}
                    width={width}
                    dangerouslySetInnerHTML={{ __html: platinumCopy.text }}
                  />
                  <Button
                    width={width}
                    href={platinumButtonUrl}
                    target="_blank"
                  >
                    {platinumCopy.button}
                  </Button>
                </BannerCard>
              </PlatinumBanner>
              <CenturionBanner paired={true}>
                <BannerCard>
                  <Title>{centurionCopy.title}</Title>
                  <Paragraph
                    paired={true}
                    width={width}
                    dangerouslySetInnerHTML={{ __html: centurionCopy.text }}
                  />
                  <Button
                    width={width}
                    href={centurionButtonUrl}
                    target="_blank"
                  >
                    {centurionCopy.button}
                  </Button>
                </BannerCard>
              </CenturionBanner>
            </BannerWrapper>
          )}

          {showPlatinumBanner(locale) && !showCenturionBanner(locale) && (
            <BannerWrapper>
              <PlatinumBanner>
                <BannerCard>
                  <Title>{platinumCopy.title}</Title>
                  <Paragraph
                    paired={true}
                    width={width}
                    dangerouslySetInnerHTML={{ __html: platinumCopy.text }}
                  />{' '}
                  <Button
                    width={width}
                    href={platinumButtonUrl}
                    target="_blank"
                  >
                    {platinumCopy.button}
                  </Button>
                </BannerCard>
              </PlatinumBanner>
            </BannerWrapper>
          )}

          {showCenturionBanner(locale) && !showPlatinumBanner(locale) && (
            <BannerWrapper>
              <CenturionBanner>
                <BannerCard>
                  <Title>{centurionCopy.title}</Title>
                  <Paragraph
                    paired={true}
                    width={width}
                    dangerouslySetInnerHTML={{ __html: platinumCopy.text }}
                  />{' '}
                  <Button
                    width={width}
                    href={centurionButtonUrl}
                    target="_blank"
                    data-width={width}
                  >
                    {centurionCopy.button}
                  </Button>
                </BannerCard>
              </CenturionBanner>
            </BannerWrapper>
          )}
          <Footer isJP={isJP} locale={locale} />
        </Container>
        <LangSelect locale={locale} />
      </Main>
    </>
  )
}
